import { Box, Chip, Grid, MenuItem, Paper, TextField, Typography } from "@mui/material";
import Input from "../../../../../../components/Input";
import ButtonComp from "../../../../../../components/Button";
import { useEffect, useMemo, useState } from "react";
import {
  allocateResources,
  getBodyRemovalResourceRecommendations,
} from "../../../../../../services/dashboard";
import { useUserStore } from "../../../../../../store/useUserStore";
import { useBodyRemovalsStore } from "../../../../../../store/useBodyRemovalsStore";
import { useVerifiedStore } from "../../../../../../store/useVerifiedStore";
import { useToast } from "../../../../../../hooks/useToast";
import Loader from "../../../../../../components/Loader";
import { useAuthStore } from "../../../../../../store/useAuthStore";
import { useSessionStore } from "../../../../../../store/useSessionStore";
import { canDriverUseVehicle } from "../../../../../../utils/fleetCompatibility";
import {
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsSectionHeader,
} from "../../../../ifas/components/OperationsUI";

export default function PickupDetails({
  pickupData,
  refreshBodyRemovals,
  DriverAndVehicleData,
}: any) {
  const role = useUserStore((state) => state.role);
  const { first_name, last_name, user_name } = useAuthStore();
  const { userName, userRole } = useSessionStore();
  const effectiveRole = userRole || role;
  const assignedByName = useMemo(() => {
    const fullName = [first_name, last_name].filter(Boolean).join(" ").trim();
    return fullName || userName || user_name || effectiveRole || "ICE-CLAIM-SERVICE";
  }, [effectiveRole, first_name, last_name, userName, user_name]);
  const { selectedBodyRemoval } = useBodyRemovalsStore();
  const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;
  const { setShowVerified, setVerifiedLabel } = useVerifiedStore();
  const { showInfoToast } = useToast();

  const isPrefilled = Boolean(DriverAndVehicleData);
  const capturedBy =
    DriverAndVehicleData?.capturedBy ||
    DriverAndVehicleData?.assignedBy ||
    role;
  const [isLoading, setIsLoading] = useState(false);
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [formData, setFormData] = useState({
    driverID: "",
    vehicleID: "",
    expectedPickupDate: "",
    expectedPickupTime: "",
    contactPersonAtLocation: "",
    contactNumber: "",
    generalAssistant: "",
    assignedBy: assignedByName,
  });
  const [nearestBranch, setNearestBranch] = useState<any>(null);
  const [recommendedDrivers, setRecommendedDrivers] = useState<any[]>([]);
  const [recommendedVehicles, setRecommendedVehicles] = useState<any[]>([]);
  const selectedDriver = recommendedDrivers.find((driver: any) => driver.driverID === formData.driverID);
  const compatibleVehicles = selectedDriver
    ? recommendedVehicles.filter((vehicle: any) => canDriverUseVehicle(selectedDriver, vehicle))
    : [];
  const hasSavedBodyLocation = Boolean(
    pickupData?.houseNumber ||
      pickupData?.streetName ||
      pickupData?.suburb ||
      pickupData?.city ||
      pickupData?.province ||
      pickupData?.postalCode ||
      pickupData?.notes,
  );

  const loadResourceRecommendations = async (showError = true) => {
    if (!bodyRemovalNumber) {
      setNearestBranch(null);
      setRecommendedDrivers([]);
      setRecommendedVehicles([]);
      return;
    }

    setIsRecommendationLoading(true);
    const response = await getBodyRemovalResourceRecommendations(bodyRemovalNumber);
    if (response?.status === 200) {
      setNearestBranch(response.data?.nearestBranch || null);
      setRecommendedDrivers(response.data?.availableDrivers || []);
      setRecommendedVehicles(response.data?.availableVehicles || []);
      setFormData((prev) => ({
        ...prev,
        driverID: "",
        vehicleID: "",
      }));
    } else {
      setNearestBranch(null);
      setRecommendedDrivers([]);
      setRecommendedVehicles([]);
      if (showError) {
        showInfoToast(
          response?.data?.message ||
            "Complete body location details to find the nearest operational branch",
        );
      }
    }
    setIsRecommendationLoading(false);
  };

  const handleAdd = async () => {
    if (!bodyRemovalNumber) {
      showInfoToast("Body removal number is missing. Please refresh and try again.");
      return;
    }

    if (
      !formData.driverID ||
      !formData.vehicleID ||
      !formData.expectedPickupDate ||
      !formData.expectedPickupTime ||
      !formData.contactPersonAtLocation ||
      !formData.contactNumber ||
      !formData.generalAssistant
    ) {
      showInfoToast("Please complete driver, vehicle and pickup assignment details.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        assignedBy: assignedByName,
      };
      const response = await allocateResources(bodyRemovalNumber, payload);
      if (response.status === 200) {
        setVerifiedLabel("Resource Allocated");
        setShowVerified(true);
        await refreshBodyRemovals("", bodyRemovalNumber);
      } else {
        showInfoToast(response.data.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!DriverAndVehicleData) return;

    setFormData((prev) => ({
      ...prev,
      expectedPickupDate: DriverAndVehicleData.expectedPickupDate || "",
      expectedPickupTime: DriverAndVehicleData.expectedPickupTime || "",
      contactPersonAtLocation:
        DriverAndVehicleData.contactPersonAtLocation || "",
      contactNumber: DriverAndVehicleData.contactNumber || "",
      generalAssistant: DriverAndVehicleData.generalAssistant || "",
      assignedBy: DriverAndVehicleData.assignedBy || assignedByName,
    }));
  }, [DriverAndVehicleData, assignedByName]);

  useEffect(() => {
    if (DriverAndVehicleData) return;
    setFormData((prev) => ({
      ...prev,
      assignedBy: assignedByName,
    }));
  }, [DriverAndVehicleData, assignedByName]);

  useEffect(() => {
    if (!bodyRemovalNumber) {
      setNearestBranch(null);
      setRecommendedDrivers([]);
      setRecommendedVehicles([]);
      return;
    }

    void loadResourceRecommendations(false);
    // Recommendations should refresh only when the selected body removal changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyRemovalNumber]);

  useEffect(() => {
    if (!DriverAndVehicleData || !recommendedDrivers.length || !recommendedVehicles.length) return;

    const selectedDriver = recommendedDrivers.find(
      (d: any) =>
        `${d.firstName} ${d.lastName}` === DriverAndVehicleData.driverName,
    );
    const selectedVehicle = recommendedVehicles.find(
      (v: any) => v.vehicleType === DriverAndVehicleData.vehicleType,
    );

    setFormData((prev) => ({
      ...prev,
      driverID: selectedDriver?.driverID || prev.driverID,
      vehicleID: selectedVehicle?.vehicleID || prev.vehicleID,
    }));
  }, [DriverAndVehicleData, recommendedDrivers, recommendedVehicles]);
  if (isPrefilled) {
    return (
      <Box>
        {isLoading && <Loader isLoading={isLoading} />}
        <OperationsSectionHeader title="Driver & Vehicle" />
        <OperationsDetailGrid>
          <OperationsDetailField
            label="Assigned Driver"
            value={DriverAndVehicleData?.driverName || formData.driverID}
          />
          <OperationsDetailField
            label="Assigned Vehicle"
            value={DriverAndVehicleData?.vehicleType || formData.vehicleID}
          />
          <OperationsDetailField
            label="Expected Pickup Date"
            value={formData.expectedPickupDate}
          />
          <OperationsDetailField
            label="Expected Pickup Time"
            value={formData.expectedPickupTime}
          />
          <OperationsDetailField
            label="Contact Person at Location"
            value={formData.contactPersonAtLocation}
          />
          <OperationsDetailField
            label="General Assistant"
            value={formData.generalAssistant}
          />
          <OperationsDetailField
            label="Contact Number"
            value={formData.contactNumber}
          />
          <OperationsDetailField label="Assigned By" value={formData.assignedBy} />
          <OperationsDetailField label="Captured By" value={capturedBy} />
          <OperationsDetailField
            label="Nearest Branch"
            value={
              DriverAndVehicleData?.nearestBranchName ||
              nearestBranch?.branchName ||
              "Not captured"
            }
          />
        </OperationsDetailGrid>

        <Box mt={2.5}>
            <OperationsSectionHeader title="Pickup Details" />
          <OperationsDetailGrid>
            <OperationsDetailField
              label="House Number"
              value={pickupData?.houseNumber}
            />
            <OperationsDetailField
              label="Street Name"
              value={pickupData?.streetName}
            />
            <OperationsDetailField label="City" value={pickupData?.city} />
            <OperationsDetailField
              label="Postal Code"
              value={pickupData?.postalCode}
            />
            <OperationsDetailField label="Suburb" value={pickupData?.suburb} />
            <OperationsDetailField
              label="Province"
              value={pickupData?.province}
            />
            <OperationsDetailField
              label="Notes"
              value={pickupData?.notes}
              span={3}
            />
          </OperationsDetailGrid>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {(isLoading || isRecommendationLoading) && (
        <Loader isLoading={isLoading || isRecommendationLoading} />
      )}
      <Grid
        container
        rowSpacing={5}
        columnSpacing={10}
        mt={1}
        textAlign="left"
        p={3}
      >
        <Grid size={{ xs: 12 }}>
          <Typography
            fontSize={20}
            fontWeight={600}
            color="primary"
            textTransform="uppercase"
          >
            Driver & Vehicle
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid #EAECF0",
              borderRadius: 2,
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box>
              <Typography fontSize={13} fontWeight={800} color="#181C27">
                Nearest operational branch
              </Typography>
              <Typography fontSize={12.5} color="#6E7482" mt={0.25}>
                Drivers and vehicles are filtered using the saved body location details below.
              </Typography>
              {nearestBranch && (
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  <Chip
                    size="small"
                    label={`Nearest branch: ${nearestBranch.branchName}`}
                    sx={{ bgcolor: "#FFF4EB", color: "#EC6E00", fontWeight: 800 }}
                  />
                  {nearestBranch.distanceKm != null && (
                    <Chip
                      size="small"
                      label={`${nearestBranch.distanceKm} km away`}
                      sx={{ bgcolor: "#EAF7EF", color: "#217A3D", fontWeight: 800 }}
                    />
                  )}
                </Box>
              )}
              {!nearestBranch && (
                <Typography fontSize={12.5} color={hasSavedBodyLocation ? "#B54708" : "#B42318"} mt={1}>
                  {hasSavedBodyLocation
                    ? "Saved body location found. Branch recommendation is pending or no matching fleet branch is available."
                    : "Complete body location details to load recommended drivers and vehicles."}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            name="driverId"
            disabled={isPrefilled}
            value={formData.driverID}
            onChange={(e: any) => setFormData({ ...formData, driverID: e.target.value, vehicleID: "" })}
            label="Assign Driver*"
            height={40}
            sx={{
              backgroundColor: isPrefilled ? "#f5f5f5" : "white",
            }}
          >
            {recommendedDrivers.map((driver: any) => (
              <MenuItem key={driver.driverID} value={driver.driverID}>
                {driver.firstName} {driver.lastName}
                {driver.branchName || driver.branchLocation
                  ? ` - ${driver.branchName || driver.branchLocation}`
                  : ""}
              </MenuItem>
            ))}
          </Input>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            select
            name="vehicleId"
            value={formData.vehicleID}
            disabled={isPrefilled || !formData.driverID}
            onChange={(e: any) =>
              setFormData({ ...formData, vehicleID: e.target.value })
            }
            label="Assign Vehicle*"
            height={40}
            sx={{
              backgroundColor: isPrefilled ? "#f5f5f5" : "white",
            }}
          >
            {compatibleVehicles.map((vehicle: any) => (
              <MenuItem key={vehicle.vehicleID} value={vehicle.vehicleID}>
                {vehicle.vehicleRegistrationNumber ||
                  vehicle.vehicleNumber ||
                  vehicle.manufacturerName}
                {vehicle.branchName || vehicle.branchLocation
                  ? ` - ${vehicle.branchName || vehicle.branchLocation}`
                  : ""}
              </MenuItem>
            ))}
          </Input>
          {!isPrefilled && (
            <Typography fontSize={12} color="#6E7482" mt={0.5}>
              {formData.driverID
                ? compatibleVehicles.length ? "Only vehicles compatible with the driver's licence and PDP are shown."
                  : "No compatible vehicles are available at this branch."
                : "Select a driver to see compatible vehicles."}
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="date"
            value={formData.expectedPickupDate}
            height={40}
            width="100%"
            label="Expected Pickup Date*"
            disabled={isPrefilled}
            onChange={(e: any) =>
              setFormData({ ...formData, expectedPickupDate: e.target.value })
            }
            sx={{
              backgroundColor: isPrefilled ? "#f5f5f5" : "white",
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            type="time"
            value={formData.expectedPickupTime}
            height={40}
            disabled={isPrefilled}
            width="100%"
            label="Expected Pickup Time*"
            onChange={(e: any) =>
              setFormData({ ...formData, expectedPickupTime: e.target.value })
            }
            sx={{
              backgroundColor: isPrefilled ? "#f5f5f5" : "white",
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            value={formData.contactPersonAtLocation}
            disabled={isPrefilled}
            height={40}
            width="100%"
            label="Contact Person at Location*"
            onChange={(e: any) =>
              setFormData({
                ...formData,
                contactPersonAtLocation: e.target.value,
              })
            }
            sx={{
              backgroundColor: isPrefilled ? "#f5f5f5" : "white",
            }}
          />
        </Grid>
         <Grid size={{ xs: 12, md: 4 }}>
          <Input
            value={formData.generalAssistant}
            disabled={isPrefilled}
            height={40}
            width="100%"
            label="General Assistant*"
            onChange={(e: any) =>
              setFormData({
                ...formData,
                generalAssistant: e.target.value,
              })
            }
            sx={{
              backgroundColor: isPrefilled ? "#f5f5f5" : "white",
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            value={formData.contactNumber}
            disabled={isPrefilled}
            height={40}
            width="100%"
            label="Contact Number*"
            onChange={(e: any) =>
              setFormData({ ...formData, contactNumber: e.target.value })
            }
            sx={{
              backgroundColor: isPrefilled ? "#f5f5f5" : "white",
            }}
          />
        </Grid>
        {!isPrefilled && (
          <Grid size={{ xs: 12 }}>
            <Box textAlign="right" width="100%">
              <ButtonComp
                variant="contained"
                height={40}
                width={150}
                onClick={handleAdd}
                disabled={isLoading || isRecommendationLoading}
              >
                Assign
              </ButtonComp>
            </Box>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={pickupData?.houseNumber}
            height={40}
            width="100%"
            label="House Number"
            backgroundColor="background.paper"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={pickupData?.streetName}
            height={40}
            width="100%"
            label="Street Name"
            backgroundColor="background.paper"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={pickupData?.city}
            height={40}
            width="100%"
            label="City"
            backgroundColor="background.paper"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={pickupData?.postalCode}
            height={40}
            width="100%"
            label="Postal Code"
            backgroundColor="background.paper"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={pickupData?.suburb}
            height={40}
            width="100%"
            label="Suburb"
            backgroundColor="background.paper"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Input
            disabled
            value={pickupData?.province}
            height={40}
            width="100%"
            label="Province"
            backgroundColor="background.paper"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography fontWeight={600} fontSize={18}>
            Notes
          </Typography>
          <TextField
            disabled
            value={pickupData?.notes}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "8px",
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
